/**
 * Utility functions for handling image URLs across the application
 */

import { environmentConfig } from '../services/universalApi'

/**
 * Generates user initials from first and last name
 */
export function getUserInitials(firstName?: string, lastName?: string, name?: string): string {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }
  
  if (name) {
    const nameParts = name.trim().split(' ')
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase()
    }
    return nameParts[0].charAt(0).toUpperCase()
  }
  
  return 'U' // Default fallback
}

/**
 * Converts a backend image URL to a frontend-accessible URL
 * Handles both relative paths (/uploads/...) and full URLs
 * Returns null if no image URL provided (for initials fallback)
 */
export function getImageUrl(imageUrl: string | null | undefined): string | null {
  if (!imageUrl) {
    return null // No fallback - let components handle initials
  }
  
  // If it's already a full URL, use it as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl
  }
  
  // If it's a relative path starting with /uploads, convert to full URL
  if (imageUrl.startsWith('/uploads')) {
    const backendUrl = environmentConfig.backendUrl
    return `${backendUrl}${imageUrl}`
  }
  
  // For any other case, return the URL as is
  return imageUrl
}

/**
 * Gets a user's profile image URL (returns null if no image for initials fallback)
 */
export function getUserProfileImageUrl(user: any): string | null {
  const photoUrl = user?.photoUrl || user?.photo || user?.photo_url
  
  // If no photo URL, return null for initials fallback
  if (!photoUrl) {
    return null
  }
  
  // Additional validation: if photoUrl is null, undefined, or empty string, return null
  if (photoUrl === null || photoUrl === undefined || photoUrl === '') {
    return null
  }
  
  // Check if it's a real uploaded image (starts with /uploads/ or contains backend URL)
  // vs a mock URL (randomuser.me, placeholder.com, etc.)
  if (photoUrl.startsWith('/uploads/') || 
      photoUrl.includes(`${environmentConfig.backendUrl}/uploads/`) ||
      photoUrl.includes('127.0.0.1:8001/uploads/')) {
    return getImageUrl(photoUrl)
  }
  
  // For any other URL (including mock URLs), treat as no image
  return null
}

/**
 * Gets a student's profile image URL (returns null if no image for initials fallback)
 */
export function getStudentProfileImageUrl(student: any): string | null {
  const photoUrl = student?.photo || student?.photoUrl || student?.studentPhoto
  
  // If no photo URL, return null for initials fallback
  if (!photoUrl) {
    return null
  }
  
  // Check if it's a real uploaded image (starts with /uploads/ or contains backend URL)
  // vs a mock URL (randomuser.me, placeholder.com, etc.)
  if (photoUrl.startsWith('/uploads/') || 
      photoUrl.includes(`${environmentConfig.backendUrl}/uploads/`) ||
      photoUrl.includes('127.0.0.1:8001/uploads/')) {
    return getImageUrl(photoUrl)
  }
  
  // For any other URL (including mock URLs), treat as no image
  return null
}

